// src/components/ranking/RankingTable.jsx
// The full ranking: everything that goes into the score, one row per player, best first.
import React from 'react';
import { CLASSIC_RANKING, formatScore } from '../../utils/ranking';

const plural = (n, word) => `${n} ${word}${Math.abs(n) === 1 ? '' : 's'}`;

// The change of place since the previous round: an up or down arrow with the number of places.
export function Movement({ value }) {
    if (value === null || value === undefined) return null;
    if (value > 0) {
        return (
            <span className="movement up" title={`Up ${plural(value, 'place')} since the previous round`} aria-label={`Up ${plural(value, 'place')}`}>
                <span aria-hidden="true">▲</span> {value}
            </span>
        );
    }
    if (value < 0) {
        return (
            <span className="movement down" title={`Down ${plural(-value, 'place')} since the previous round`} aria-label={`Down ${plural(-value, 'place')}`}>
                <span aria-hidden="true">▼</span> {-value}
            </span>
        );
    }
    return (
        <span className="movement same" title="Same place as before the previous round" aria-label="Same place">
            <span aria-hidden="true">–</span>
        </span>
    );
}

const signed = (n) => (n > 0 ? `+${n}` : String(n));

// name: the header, hint: what it means (shown as a tooltip)
export const COLUMNS = [
    { key: 'score', name: 'Score', hint: 'What the ranking is based on. See "How is the score calculated?".' }, // named by the method
    { key: 'matches', name: 'Matches', hint: 'Matches won – lost (draws in brackets)' },
    { key: 'sets', name: 'Sets', hint: 'Sets won – lost' },
    { key: 'points', name: 'Points', hint: 'Points scored – points conceded' },
    { key: 'diff', name: '+/−', hint: 'Points scored minus points conceded. Helps to decide between equal scores.' },
    { key: 'games', name: 'Games', hint: 'Rounds played' },
    { key: 'breaks', name: 'Breaks', hint: 'Rounds sat out' },
    { key: 'repeatOpponents', name: 'Rep. opp.', hint: 'Times this player met an opponent they had already met' },
    { key: 'repeatTeammates', name: 'Rep. mates', hint: 'Times this player was paired with a teammate they had already had' },
];

export function cell(row, key, ranking) {
    switch (key) {
        case 'score':
            return <strong>{formatScore(row.score, ranking)}</strong>;
        case 'matches':
            return `${row.matchesWon}–${row.matchesLost}${row.matchesDrawn ? ` (${row.matchesDrawn})` : ''}`;
        case 'sets':
            return `${row.setsWon}–${row.setsLost}`;
        case 'points':
            return `${row.pointsFor}–${row.pointsAgainst}`;
        case 'diff':
            return signed(row.pointDiff);
        case 'games':
            return row.games;
        case 'breaks':
            return row.breaks;
        default:
            return row[key];
    }
}

function RankingTable({ standings, ranking = CLASSIC_RANKING, highlightId = null }) {
    return (
        <div className="table-scroll">
            <table className="ranking-table">
                <thead>
                    <tr>
                        <th scope="col" className="rank-col">
                            #
                        </th>
                        <th scope="col" className="name-col">
                            Player
                        </th>
                        {COLUMNS.map((column) => (
                            <th key={column.key} scope="col" title={column.hint} className={column.key === 'score' ? 'score-col' : ''}>
                                {column.key === 'score' ? ranking.unit : column.name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {standings.map((row) => (
                        <tr key={row.playerId} className={`${row.active ? '' : 'inactive'}${row.playerId === highlightId ? ' me' : ''}`.trim()}>
                            <td className="rank-col">{row.rank}</td>
                            <th scope="row" className="name-col">
                                <span className="ranking-name">
                                    {row.firstName} {row.lastName}
                                </span>
                                {!row.active && <span className="badge">Inactive</span>}
                                <Movement value={row.movement} />
                            </th>
                            {COLUMNS.map((column) => (
                                <td key={column.key} className={column.key === 'score' ? 'score-col' : ''}>
                                    {cell(row, column.key, ranking)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default RankingTable;
